/**
 * The newest messages of a conversation that has to be read from the Wix API (it has nothing in Firestore),
 * kept on the device (IndexedDB) so reopening it shows them at once instead of repeating that fetch. The thread
 * refreshes from Wix in the background afterwards. Capped; the least recently written conversations are dropped.
 */

const DB_NAME = 'rs-thread-snapshots';
const STORE = 'threads';
const MAX_ENTRIES = 60;

export interface ThreadSnapshot {
  messages: unknown[];
  nextCursor?: string;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      try {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
          const store = request.result.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('at', 'at');
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
  return dbPromise;
}

/** The stored snapshot for a conversation, or null. Never rejects. */
export async function readThreadSnapshot(id: string): Promise<ThreadSnapshot | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(id);
      request.onsuccess = () => {
        const row = request.result as { snapshot?: ThreadSnapshot } | undefined;
        resolve(row?.snapshot ?? null);
      };
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Remember a conversation's newest messages on the device (fire and forget). */
export function writeThreadSnapshot(id: string, snapshot: ThreadSnapshot): void {
  void openDb().then((db) => {
    if (!db) return;
    try {
      const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
      store.put({ id, snapshot, at: Date.now() });
      const countRequest = store.count();
      countRequest.onsuccess = () => {
        let extra = countRequest.result - MAX_ENTRIES;
        if (extra <= 0) return;
        const cursorRequest = store.index('at').openCursor();
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor && extra-- > 0) {
            cursor.delete();
            cursor.continue();
          }
        };
      };
    } catch {
      /* storage unavailable or not cloneable — not worth failing a render over */
    }
  });
}
